import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';
import { FileGallery, fileUrl, fmtDate, fmtDay } from 'lib/adminDetail';

const productNames = (list) =>
  (Array.isArray(list) ? list : [])
    .map((p) => p?.name || p?.title || p)
    .filter(Boolean)
    .join(', ') || '—';

const CouponsPage = () => {
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/coupon', { params: { limit: 100 } });
      const list = res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const filtered = useMemo(() => {
    let list = rows;
    if (typeFilter !== 'all') {
      list = list.filter(
        (c) =>
          String(c.discount_type || c.coupon_type || '').toUpperCase() ===
          typeFilter,
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      `${c.title || ''} ${c.name || ''} ${c.description || ''} ${
        c.outlet_id?.name || ''
      } ${c.created_by?.full_name || ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search, typeFilter]);

  const columns = [
    {
      title: 'Coupon',
      key: 'title',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <strong>{r.title || r.name || 'Untitled'}</strong>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {(r.description || '').slice(0, 80)}
          </span>
        </Space>
      ),
    },
    {
      title: 'Outlet',
      key: 'outlet',
      render: (_, r) => r.outlet_id?.name || '—',
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, r) => (
        <Tag color="blue">{r.discount_type || r.coupon_type || '—'}</Tag>
      ),
    },
    {
      title: 'Value',
      key: 'value',
      render: (_, r) => r.value ?? r.discount_value ?? '—',
    },
    {
      title: 'Validity',
      key: 'valid',
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {fmtDay(r.valid_from)} → {fmtDay(r.valid_to)}
          {r.valid_time_from
            ? ` · ${r.valid_time_from}–${r.valid_time_to || '—'}`
            : ''}
        </span>
      ),
    },
    {
      title: 'Stock',
      key: 'left',
      render: (_, r) => (
        <span>
          {r.left_count == null ? '—' : r.left_count}
          {r.count != null ? ` / ${r.count}` : ''}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) =>
        r.isExpired || (r.valid_to && new Date(r.valid_to) < new Date()) ? (
          <Tag>Expired</Tag>
        ) : r.active === false ? (
          <Tag color="red">Inactive</Tag>
        ) : (
          <Tag color="green">Active</Tag>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(record);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const r = selected;
  const img = r ? fileUrl(r.image) : null;

  return (
    <PageShell
      title="Coupons"
      description="Every merchant offer: outlet, dates, limits, terms, and products."
      actions={
        <Button icon={<ReloadOutlined />} onClick={fetchCoupons} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Search title, outlet, merchant"
            allowClear
            style={{ width: 300 }}
            onSearch={setSearch}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'All types' },
              { value: 'PERCENTAGE', label: 'Percentage' },
              { value: 'FLAT', label: 'Flat' },
              { value: 'LOTTERY', label: 'Lottery' },
            ]}
          />
        </Space>
        <Table
          rowKey={(row) => row._id}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onRow={(record) => ({
            onClick: () => setSelected(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={r?.name || r?.title || 'Coupon details'}
        open={!!r}
        onCancel={() => setSelected(null)}
        footer={[
          <Button key="close" onClick={() => setSelected(null)}>
            Close
          </Button>,
        ]}
        width={760}
      >
        {r && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Name" span={2}>
                {r.name || r.title || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {r.description || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Outlet">
                {r.outlet_id?.name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Created by">
                {r.created_by?.full_name || '—'}
                {r.created_by?.mobile_number
                  ? ` · ${r.created_by.mobile_number}`
                  : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {r.discount_type || '—'} / {r.coupon_type || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Value">{r.value ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Buy X">{r.buy_x ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Min order">
                {r.minimum_order_price ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Max benefit">
                {r.maximum_benefits ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Per-user limit">
                {r.max_user_limit ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Count / left">
                {r.count ?? '—'} / {r.left_count ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Valid from">{fmtDate(r.valid_from)}</Descriptions.Item>
              <Descriptions.Item label="Valid to">{fmtDate(r.valid_to)}</Descriptions.Item>
              <Descriptions.Item label="Hours">
                {r.valid_time_from || '—'} – {r.valid_time_to || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Flags">
                {[
                  r.recurring && 'Recurring',
                  r.new_user && 'New users',
                  r.luckyDrawAnnounced && 'Lucky draw announced',
                  r.active === false && 'Inactive',
                ]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Products" span={2}>
                {productNames(r.products)}
              </Descriptions.Item>
              <Descriptions.Item label="Buy products">
                {productNames(r.buy_products)}
              </Descriptions.Item>
              <Descriptions.Item label="Get products">
                {productNames(r.get_products)}
              </Descriptions.Item>
              <Descriptions.Item label="Terms" span={2}>
                {r.terms_conditions || '—'}
              </Descriptions.Item>
              {r.lottery_conditions && (
                <Descriptions.Item label="Lottery" span={2}>
                  Min purchase {r.lottery_conditions.minPurchase ?? 0} · Max
                  participants {r.lottery_conditions.maxParticipants ?? 0}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Created">{fmtDate(r.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Updated">{fmtDate(r.updatedAt)}</Descriptions.Item>
            </Descriptions>
            {img ? (
              <div style={{ marginTop: 16 }}>
                <FileGallery title="Coupon image" files={[img]} />
              </div>
            ) : null}
          </>
        )}
      </Modal>
    </PageShell>
  );
};

export default CouponsPage;
